import { Grid, Link, ListItemButton, Stack, Typography } from '@mui/material';
import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { formatNumber, NumberFormatPreset, useCurrency } from '@procureai/ui-mui';
import { secondsToMilliseconds } from 'date-fns';
import { isNotNil } from 'ramda';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Union } from 'ts-toolbelt';

import { useTrackSearchResults } from '../../hooks/useTrackSearchResults/useTrackSearchResults.ts';
import { SearchIndexDates, SearchIndexId, SearchIndexSourceRefence } from '../../types/SearchIndex.ts';
import { Target } from '~/hooks/useTrackInteraction/useTrackInteraction';
import { RouteId } from '~/routes/RouteId';
import { eventStopPropagation } from '~/util/function/eventStopPropagation';

export interface SearchHitContractHit extends SearchIndexId, SearchIndexDates, SearchIndexSourceRefence {
  title: string;
  unitId: string;
  commodityGroupId: Union.Nullable<string>;
  createdAtTimestamp: number;
  pricePerUnitFloat: number;
  plannedQuantityFloat: Union.Nullable<number>;
  plannedValueFloat: Union.Nullable<number>;
  parent: {
    id: string;
    primarySourceReference: string;
    status: Union.Nullable<string>;
    supplierId: Union.Nullable<string>;
    currency: string;
    validFrom: string;
    validFromTimestamp: number;
    validTo: string;
    validToTimestamp: number;
  };
}

interface SearchHitContractProps {
  hit: SearchHitContractHit;
}

export const SearchHitContract = ({ hit }: SearchHitContractProps) => {
  const { t: tHit } = useTranslation(undefined, { keyPrefix: 'SearchHitContract' });
  const { t: tDialog } = useTranslation(undefined, { keyPrefix: 'SearchDialog' });
  const navigate = useNavigate();
  const { format } = useCurrency({ code: hit.parent.currency });

  const itemLink = RouteId.contractDetail(hit.parent.id);

  const handleItemClicked = useTrackSearchResults({ hit, target: Target.SEARCH_RESULT_CONTRACT });

  return (
    <ListItemButton
      onClick={(event) => {
        handleItemClicked(event);
        navigate(itemLink);
      }}
    >
      <Grid container sx={{ flexGrow: 1 }}>
        <Grid size={9}>
          <Link component={RouterLink} to={itemLink} variant="subtitle1" onClick={handleItemClicked} underline="hover">
            {hit.title}
          </Link>
          <Typography
            component="div"
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {tHit('date', { createdAt: secondsToMilliseconds(hit.createdAtTimestamp) })}
          </Typography>
          <Stack
            direction="row"
            sx={{
              flexWrap: 'wrap',
              columnGap: 2,
              marginTop: 1,
            }}
          >
            {hit.parent.supplierId ? (
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {tHit('supplier')}
                </Typography>
                <Link
                  component={RouterLink}
                  to={RouteId.supplierDetail(hit.parent.supplierId)}
                  variant="body2"
                  onClick={eventStopPropagation}
                >
                  <CuriosityValue ns={CuriosityNamespaceType.SUPPLIER} i18nKey={hit.parent.supplierId} />
                </Link>
              </Stack>
            ) : null}
            {hit.commodityGroupId ? (
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {tHit('commodityGroup')}
                </Typography>
                <Link
                  component={RouterLink}
                  to={RouteId.commodityGroupDetail(hit.commodityGroupId)}
                  variant="body2"
                  onClick={eventStopPropagation}
                >
                  <CuriosityValue ns={CuriosityNamespaceType.COMMODITY_GROUP} i18nKey={hit.commodityGroupId} />
                </Link>
              </Stack>
            ) : null}
            {hit.parent.status ? (
              <Stack direction="row" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {tDialog('SearchResultGroup.status')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.primary',
                  }}
                >
                  {hit.parent.status}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </Grid>
        <Grid size={3}>
          <Typography
            variant="subtitle1"
            sx={{
              textAlign: 'right',
            }}
          >
            {hit.plannedValueFloat ? format(hit.plannedValueFloat) : tHit('emptyValue')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'right',
            }}
          >
            <Trans
              t={tHit}
              i18nKey="amountUnitPrice"
              values={{
                amount: isNotNil(hit.plannedQuantityFloat)
                  ? formatNumber(NumberFormatPreset.DECIMAL, hit.plannedQuantityFloat)
                  : tHit('emptyValue'),
                price: hit.pricePerUnitFloat ? format(hit.pricePerUnitFloat) : tHit('emptyValue'),
              }}
              components={{
                CuriosityValue: <CuriosityValue ns={CuriosityNamespaceType.UNIT} i18nKey={hit.unitId} />,
              }}
            />
          </Typography>
        </Grid>
      </Grid>
    </ListItemButton>
  );
};
